import Typography from '@mui/material/Typography';

import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { Divider, Link, Box, Accordion, AccordionSummary, AccordionDetails, Paper  } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

const IssuesOfInterest = (): JSX.Element => {

  document.title = 'OCPF - Issues of Interest';

  const issues = [
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Citizens United and Independent Expenditure PACs
        </Typography>
      ),
      body: <>
        <Typography component={'p'} variant="body1" marginBottom={2}>
        In <strong>Citizens United v. Federal Election Commission</strong>, 558 U.S. 310 (2010), the United States Supreme Court ruled that <strong>independent expenditures</strong> by corporations made to influence candidate elections cannot be limited, because doing so would not comply with the First Amendment. Although the decision concerned the application of a federal statute, it also calls into question OCPF’s longstanding interpretation of M.G.L. Chapter 55, Section 8 as prohibiting both contributions and independent expenditures by corporations to support of oppose candidates or political committees.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
        Section 8 states, in part, that business corporations, and other entities specified in Section 8, may not “<strong>expend</strong> or contribute, any money or other valuable thing for the purpose of aiding, promoting or preventing the nomination or election of any person to public office, or aiding or promoting or antagonizing the interest of any political party.” (Emphasis added). In light of <strong>Citizens United</strong>, the office will apply Section 8 as follows: Corporations or other entities named in the statute <strong>may still not make contributions</strong> to support or oppose candidates or political parties. In addition, if a corporation or other entity which may not contribute under Section 8 makes an independent expenditure, a report disclosing the independent expenditure must be filed, as required by M.G.L. Chapter 55, Section 18A.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
        If a corporation or its agents make an expenditure not “in concert with, or at the request or suggestion of, any candidate, or any nonelected political committee organized on behalf of a candidate or agent of such candidate” it is an independent expenditure. See M.G.L. Chapter 55, Sections 1 and 18A. On the other hand, a corporate expenditure that is made in concert with, or at the request or suggestion of, a candidate, would be prohibited by Section 8.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
        A group organized to raise money for the purpose of making independent expenditures is required to organize as an Independent Expenditure PAC. These committees are sometimes called "superpacs." OCPF has issued regulations (<Link href="/legal/regulations" target="_blank">970 CMR 2.17</Link>) and an interpretive bulletin (<Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-03.pdf" target="_blank">IB-10-03</Link>) to provide guidance to such groups.
      </Typography>
      <Typography component={'p'} variant="body1">
        Click here to read the full decision.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Subvendor Reporting
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      <Link href="/legal/campaignfinancelaw" target="_blank">Section 18D</Link> requires vendors who receive $5,000 or more from a political committee to provide the committee with a detailed account of all subsequent expenditures of $500 or more that the vendors, on behalf of the political committee, make to subvendors who provide goods or services to the vendor. A committee must electronically file subvendor reports based on information provided to the committee by the vendors. OCPF has issued regulations (<Link href='/legal/regulations' target='_blank'>970 CMR 2.18(2)</Link>) and <Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-04.pdf" target="_blank">IB-10-04</Link> to provide guidance regarding this requirement.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Late Contribution Reporting
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      <Link href="/legal/campaignfinancelaw" target="_blank">Section 18</Link> states that every political committee organized on behalf of a candidate that files with OCPF and every ballot question committee that files with OCPF, “which receives and deposits a contribution in the amount of $500 or more after the eighteenth day, but more than 72 hours, before the date of a special, preliminary, primary or general election, shall file a report” to disclose the contributions within 72 hours of depositing the contribution. OCPF has issued regulations (<Link href='/legal/regulations' target='_blank'>970 CMR 1.18</Link>) and <Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-04.pdf" target="_blank">IB-10-02</Link> to provide guidance regarding this requirement.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Electioneering Communication
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      <Link href="/legal/campaignfinancelaw" target="_blank">Section 18F</Link> requires individuals or groups (other than political committees) to disclose electioneering communications. Electioneering communications are defined in Section 1 of the statute, and OCPF has issued a regulation (<Link href="/legal/regulations" target="_blank">970 CMR 1.14</Link>) to provide guidance regarding this requirement.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Legal Defense, Inauguration and Recount Funds <Typography variant="caption">(Segregated Funds)</Typography>
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      <Link href="/legal/campaignfinancelaw" target="_blank">Section 18E</Link> allows candidates to create separate legal defense, recount or inauguration funds and state political parties to create legal defense funds. OCPF has issued regulations (<Link href="/legal/regulations" target="_blank">970 CMR 1.20</Link>) and a Memorandum (<Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/M-10-02.pdf" target="_blank">M-10-02</Link>) to provide guidance regarding the creation of such funds.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Public Resources and Ballot Questions
        </Typography>
      ),
      body: <>
        <Typography component={'p'} variant="body1" marginBottom={2}>
          The campaign finance law prohibits the use of public resources to influence voters in an election, including ballot questions. OCPF is often asked about the application of the prohibition to Proposition 2½ override and debt exclusion questions. This section provides an overview of the issue, with links to other OCPF materials that may prove helpful in answering your questions.
        </Typography>
        <Typography component={'p'} variant="body1" marginBottom={2}>
        In the 1978 case of <u>Anderson v. City of Boston</u>, the Supreme Judicial Court ruled that public resources may not be used for political campaign purposes. The court found that the city's appropriation of almost $1 million for an organized effort to convince voters to support a state ballot question was prohibited by the campaign finance law, M.G.L. c.55.
        </Typography>
        <Typography component={'p'} variant="body1" marginBottom={2}>
        Though the Anderson decision came two years before the passage of Proposition 2½, it has its most common impact in recent years in local override and debt exclusion elections. Cities and towns may not, according to Anderson, use public resources to persuade voters to support or oppose a question put to voters in an election. In addition, the state Constitution prohibits the publicly funded distribution of any information to voters concerning a ballot question without express statutory authority.
        </Typography>
        <Typography component={'p'} variant="body1" marginBottom={2}>
        Public Resources includes anything that is paid for through public funds (taxes or fees), such as paper, postage, staff time, equipment and property.
        </Typography>
        <Typography component={'p'} variant="body1" marginBottom={2}>
        The campaign finance law does not, however, prohibit public officials and employees from participating in the public discussion of issues, holding and attending forums and meetings, and supporting or joining ballot question committees. It also does not prohibit the use of public facilities by political groups such as ballot question committees, as long as a facility is available to all such groups under the same terms and conditions. In addition, it should be noted that the Anderson limitations apply to elections only: activities concerning town meeting only are not regulated. In the event of questions that are on both the town meeting warrant and the election ballot, however, the prohibition may apply.
        </Typography>
        <Typography component={'p'} variant="body1" marginBottom={2}>
          OCPF has developed several publications to provide further explanation of the public resources issue:
          <ul>
            <li>
              <Typography variant="body1">
                <Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/B-91-01.pdf" target="_blank">Interpretive Bulletin 91-01</Link>: The Application of the Campaign Finance Law to the Use of Governmental Resources for Political Purposes
               </Typography>
            </li>
            <li>
              <Typography variant="body1">
              <Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/B-91-01.pdf" target="_blank">Interpretive Bulletin 92-02</Link>: Activities of Public Officials in Support of or Opposition to Ballot Questions.
               </Typography>
            </li>
            <li>
              <Typography variant="body1">
              Campaign Finance <Link href="https://ocpf2.blob.core.windows.net/pdf/guides/guide_pub_emp.pdf" target="_blank">Guide</Link>: Public Employees, Public Resources and Political Activity, which is intended to answer questions concerning political and fundraising activity by government workers. A one-page <Link href="https://ocpf2.blob.core.windows.net/pdf/guides/fs_pub_emp.pdf" target="_blank">fact sheet</Link> summarizing the guide is also available.
               </Typography>
            </li>
          </ul>
          The office has also issued several advisory opinions dealing with the public resources issue. To review the opinions, use the search term "public resources" in our <Link href="/legal/search" target="_blank">online searchable database</Link>.
        </Typography>
        <Typography component={'p'} variant="body1">
        Officials and other parties with further questions are encouraged to contact OCPF.
        </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Public Employees, Public Resources and Political Activity
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      The campaign finance law prohibits the use of public resources to influence voters in an election, including ballot questions. OCPF is often asked about the application of the prohibition to Proposition 2½ override and debt exclusion questions. This section provides an overview of the issue, with links to other OCPF materials on this issue.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={3}>
      M.G.L. Chapter 55, the Campaign Finance Law, regulates political activity by public employees and the use of public buildings and resources in campaigns. Public employees who take part in political campaigns and the candidates and committees they support should be aware of these sections of the law.
      </Typography>
      <Typography fontWeight="bold" variant="subtitle1" marginBottom={2}>
      Section 13: Public Employees
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      No person employed for compensation by agencies of the Commonwealth, its cities, towns and counties, other than an elected official, may directly or indirectly solicit or receive a contribution or anything of value for any political purpose (e.g., candidates, parties, PACs, ballot question committees).
      </Typography>
      <Box component={Paper} p={2} marginBottom={2}>
        <Typography component={'p'} variant="body1" marginBottom={2}>
        A public employee <strong>may not</strong>:
        <ul>
          <li>
          sell tickets to a political fundraiser or otherwise solicit or collect political contributions in any manner, including in person, by phone, by e-mail or by conventional mail.
          </li>
          <li>
          sponsor or host a political fundraising event.
          </li>
          <li>
          allow his or her name to be used in a fundraising letter, advertisement, phone call or e-mail.
          </li>
          <li>
          help identify people to be targeted for political fundraising.
          </li>
          <li>
          serve as treasurer of a political committee.
          </li>
          <li>
          use social media to solicit political contributions, e.g., by administering a Facebook page that solicits contributions. See <Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-04-01.pdf" target="_blank">IB-04-01</Link>.
          </li>
        </ul>
        </Typography>
      </Box>
      <Box component={Paper} p={2} marginBottom={3}>
        <Typography component={'p'} variant="body1" marginBottom={2}>
        A public employee <strong>may</strong>:
        <ul>
          <li>
          contribute to candidates and attend fundraisers.
          </li>
          <li>
          run for office (a employee must organize a campaign committee if he or she plans to raise any money).
          </li>
          <li>
          work for campaigns and committees in a non-fundraising capacity, such as holding signs, stuffing envelopes, hosting coffees or other meetings, or being a member of a committee.
          </li>
        </ul>
        </Typography>
      </Box>
      <Typography fontWeight="bold" variant="subtitle1" marginBottom={2}>
      Section 14: Government Buildings
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      Soliciting or receiving campaign contributions in a government building is prohibited. Examples include city and town halls, public schools, libraries, police and fire stations and public works buildings.
      </Typography>
      <Box component={Paper} p={2} marginBottom={3}>
        <Typography component={'p'} variant="body1" marginBottom={2}>
        No one (not just public employees) may:
        <ul>
          <li>
          sell tickets to a fundraiser or otherwise solicit or collect political contributions in a public building.
          </li>
          <li>
          send a solicitation into a government building, such as by phone, mail or e-mail.
          </li>
          <li>
          use a public building as the site of a fundraiser, the return address for contributions or the contact phone number for buying tickets to a fundraiser.
          </li>
          <li>
          post in a public building any advertisement for a fundraiser.
          </li>
        </ul>
        </Typography>
      </Box>
      <Typography fontWeight="bold" variant="subtitle1" marginBottom={2}>
      Use of Public Resources
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      Public resources (government vehicles, office equipment and supplies and the paid time of public employees) may not be used for political campaign purposes, such as the election of a candidate or the passage or defeat of a ballot question. For example, a public employee may not, during his work day, render campaign service to a candidate or ballot question committee or use office postage or equipment to distribute campaign material.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      For more information, download our <Link href="https://ocpf2.blob.core.windows.net/pdf/guides/guide_pub_emp.pdf" target="_blank">Campaign Finance Guide: Public Employees, Public Resources and Political Activity</Link>.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Disclosure Required by Mayoral Candidates
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      All mayoral candidates file with OCPF in the depositoy bank reporting system, due to a 2019 change to the campaign finance law.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Contributions by Credit or Debit Card
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      Candidates and committees may receive contributions by credit or debit card, subject to certain statutory and regulatory restrictions.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      Committees receiving such contributions must use a merchant provider to process transactions and receive funds from a card holder's card company. A debit card contribution is processed in a similar fashion, except the funds come from a contributor's designated bank account, not a credit card provider.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      Recipient committees must report the gross amount of a contribution, not the net amount that is forwarded by the merchant provider after taking its fee. For example, while an individual may make a $100 contribution by credit card, a smaller amount is actually received by the committee. Nevertheless, the committee's campaign finance report should disclose a contribution of $100 from that individual.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      For more information, see OCPF's regulations concerning credit card contributions (<Link href="/legal/regulations" target="_blank">970 CMR 1.09</Link>) and Memorandum <Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/M-04-01.pdf" target="_blank">M-04-01</Link>: Contributions to Political Committees by Credit or Debit Card.
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      Those with further questions about the credit or debit card process are advised to contact OCPF by phone (617-979-8300) or <Link href="mailto:ocpf@mass.gov" title="ocpf@mass.gov">e-mail</Link>.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Limitations on Election Officials Serving as Officers of Political Committees
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      Section 5A of the campaign finance law states that:
      <Box component={'blockquote'} sx={{borderLeft:'5px solid #eeeeee'}}> 
      <Typography component={'p'} variant="body1" marginBottom={2} paddingLeft={2}>
      No candidate or individual holding elective public office shall establish, finance, maintain, control or serve as a principal officer of a political action committee; provided, however, that each of the following may authorize one such political committee to which this section shall not apply: a majority of the members of each political party who are members of the house of representatives, and a majority of the members of each political party who are members of the senate.
      </Typography>
      </Box>
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      In addition, Section 5 prohibits the Secretary of the Commonwealth, any city or town clerk who conducts elections, or any election commissioner or registrar of voters from serving as the chair, treasurer or "other principal officer" of any political committee, including candidate committees, PACs and party committees (excluding his or her own candidate committee).
      </Typography>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      The language of the relevant section is:
      </Typography>
      <Box component={'blockquote'} sx={{borderLeft:'5px solid #eeeeee'}}> 
      <Typography component={'p'} variant="body1" marginBottom={2} paddingLeft={2}>
      The state secretary, a city or town clerk, or a member of a board of registrars of voters or election commission in any city or town shall not serve as the chairman, treasurer, or other principal officer of any political committee, but any such public officer may serve as the chairman or principal officer, other than treasurer, of the political committee organized on his own behalf. This paragraph shall not apply to city or town clerks who do not administer elections.
      </Typography>
      </Box>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      Those with further questions are encouraged to contact OCPF.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Political Committees and Associations in Municipal Elections
        </Typography>
      ),
      body: <>
            <Typography component="p" variant="body1" marginBottom={2}>
        Various groups become involved in municipal elections, including ballot questions such as Proposition 2½ overrides. In some circumstances, groups are required to register as political committees and disclose their election activity under the Massachusetts campaign finance law; in other circumstances, no disclosure is required. It is therefore important to know the various features of these groups and their legal obligations.
      </Typography>
      <Typography component="p" variant="body1" marginBottom={3}>
        M.G.L. c. 55, the Campaign Finance law, requires groups to register as political committees if they raise money to support or oppose candidates, ballot questions, or political parties. These committees must file periodic campaign finance reports with the town clerk or election commission and are subject to a number of restrictions. In contrast, associations that are not political committees are not subject to these requirements. Individuals and groups interested in becoming involved in municipal elections should be aware of the distinctions among these groups:
      </Typography>

      <Box component={Paper} p={2} marginBottom={2}>
        <Typography fontWeight="bold" variant="subtitle1" marginBottom={2}>
          Ballot Question Committee
        </Typography>
        <Typography component="p" variant="body1" marginBottom={2}>
          A political committee that is organized to support or oppose a specific ballot question, such as a tax override or debt exclusion (not a town meeting article).
        </Typography>
        <ul>
          <li>
            A group that solicits or receives contributions to support or oppose a ballot question must register with the town clerk or election commission and file disclosure reports before and after the election.
          </li>
          <li>
            Ballot question committees may receive contributions in any amount and from any source, including corporations.
          </li>
          <li>
            Ballot question committees must dissolve after the relevant election and dispose of any residual funds by making a payment to a charitable or other entity specified in the statute.
          </li>
        </ul>
      </Box>

      <Box component={Paper} p={2} marginBottom={2}>
        <Typography fontWeight="bold" variant="subtitle1" marginBottom={2}>
          Political Action Committee (PAC)
        </Typography>
        <Typography component="p" variant="body1" marginBottom={2}>
          A political committee that is organized to support or oppose candidates.
        </Typography>
        <ul>
          <li>
            Like ballot question committees, PACs file periodic campaign finance disclosure reports.
          </li>
          <li>
            Unlike ballot question committees, PACs may not solicit or receive contributions from corporations and may accept no more than $500 from any individual in a calendar year.
          </li>
          <li>
            A PAC may remain in existence indefinitely but must file periodic campaign finance reports.
          </li>
        </ul>
      </Box>

      <Box component={Paper} p={2} marginBottom={2}>
        <Typography fontWeight="bold" variant="subtitle1" marginBottom={2}>
          Association
        </Typography>
        <Typography component="p" variant="body1" marginBottom={2}>
          A previously existing or new group formed to influence officials or the public concerning a public policy (non-election) issue (sometimes referred to as an issue group).
        </Typography>
        <ul>
          <li>
            An association may be organized to keep residents informed regarding a particular issue, influence town meetings, or lobby selectmen or other municipal committees or boards.
          </li>
          <li>
            An association has no campaign finance filing requirements as long as it is not involved in supporting or opposing a ballot question, candidates, or a political party.
          </li>
          <li>
            An association may make expenditures to support or oppose a ballot question from its general treasury without having to organize a ballot question committee. However, if funds are raised specifically for ballot question activity, it must register as a ballot question committee and segregate those funds.
          </li>
          <li>
            As long as an association is not raising funds for the purpose of supporting or opposing a ballot question or candidates, it is not subject to any contribution limits and is not required to file reports under the campaign finance law.
          </li>
          <li>An association may remain in existence indefinitely.</li>
        </ul>
      </Box>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Contributions to PACs by Payroll Deduction
        </Typography>
      ),
      body: <>
      <Typography component="p" variant="body1" marginBottom={2}>
        <Link href="/legal/campaignfinancelaw" target="_blank">Section 9A</Link> allows electronic transfers of funds to a PAC from a contributor's funds through his or her employer, such as deductions from wages. The maximum that can be contributed by payroll deduction is $500, which is also the maximum that can be given to a PAC by an individual. Aggregate contributions to any individual PAC above $50, up to and including $500 in the aggregate, may only be made by either personal check or electronic transfer, such as payroll deduction.
      </Typography>
      <Typography component="p" variant="body1" marginBottom={2}>
        Section 9A allows such transfers to be made only after the contributor completes a card authorizing such an action and submits it to the bank or employer. The authorization may also be revoked at any time by the contributor, also in writing.
      </Typography>
      <Typography component="p" variant="body1" marginBottom={2}>
        Those with further questions on the statute should contact OCPF.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Campaign Uses of the Internet and E-Mail
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      In recent years there has been a surge in the political use of the Internet and e-mail. OCPF Interpretive Bulletin <Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-04-01.pdf" target="_blank">IB-04-01</Link> answers the most frequently asked questions regarding the extent to which the Internet and e-mail may be utilized by candidates, political committees or other persons in connection with elections occurring in Massachusetts, including an in-depth discussion of the expenditures that may be made for Internet access, the services that may be provided to candidates or committees to help them establish websites, the use of links to campaign websites, and access to government websites and e-mail networks, and limitations relating to the political use of social media.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Adjusted People's Committee Contribution and Bundling Limits for 2014 and 2015
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      The campaign finance law requires the director of OCPF to adjust two specific contribution limits every two years based upon the Consumer Price Index (CPI) calculated for the Greater Boston area. The first is the limit on the amount an individual may contribute to a people's committee during a calendar year (<Link href="/legal/campaignfinancelaw" target="_blank">M.G.L. c. 55, § 1</Link>). The second is the amount of any individual contribution that may be collected, i.e. bundled, by a regulated intermediary or conduit before certain disclosure requirements of the campaign finance law are triggered (<Link href="/legal/campaignfinancelaw" target="_blank">M.G.L. c. 55, § 10A</Link>). The limit in effect until December 2015 is $161.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Express Advocacy and Issue Advocacy
        </Typography>
      ),
      body: <>
      <Typography component={'p'} variant="body1" marginBottom={2}>
      OCPF Interpretive Bulletin <Link href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-06-01.pdf" target="_blank">IB-06-01</Link> answers the most frequently asked questions regarding the applicability of the Massachusetts campaign finance law, <Link href="/legal/campaignfinancelaw" target="_blank">M.G.L. c. 55</Link>, to various types of advertisements or other communications that might be viewed as "issue oriented," but which might also be seen as praising or disparaging a candidate and provides guidance regarding whether such communications involve the making of expenditures subject to the requirements of the Massachusetts campaign finance law.
      </Typography>
      </>
    },
    {
      title: (
        <Typography component={'h2'} variant="h6">
          Limited Public Financing of Campaigns for Statewide Office
        </Typography>
      ),
      body: <>
        <Typography component="p" variant="body1" marginBottom={2}>
        The candidates for the six statewide offices in Massachusetts (Governor, Lieutenant Governor, Attorney General, Treasurer, Secretary, and Auditor) are eligible to receive partial public funding of their campaigns in return for agreeing to spending limits.
      </Typography>
      <Typography component="p" variant="body1" marginBottom={2}>
        This program has been in existence in some form since the 1970s. The program is voluntary. However, all candidates must declare whether they plan to participate by the time they file nomination papers. <strong>Any candidate who does not file the required form with OCPF is barred from appearing on the ballot.</strong>
      </Typography>
      <Typography component="p" variant="body1" marginBottom={2}>
        The spending limits are set by <Link href="/Legal/PublicFinancingLaw" target="_blank">M.G.L. Chapter 55C</Link> and vary according to the office sought, but they are subject to adjustment: those who do not choose to participate must, if they are opposed by a participating candidate, notify OCPF of their anticipated maximum spending in the upcoming campaign. If the planned spending by a non-participant is higher than the participant's statutory limit for that office, the participant's limit is increased to the highest figure cited by a non-participant.
      </Typography>
      <Typography component="p" variant="body1" marginBottom={2}>
        Massachusetts does not have a similar program for other non-statewide candidates, so currently there are no public funds available to candidates for other offices, such as the Legislature or county posts.
      </Typography>

      <Box component={Paper} p={2} marginBottom={3}>
        <Typography fontWeight="bold" variant="subtitle1" marginBottom={2}>
          New Public Financing Materials Available
        </Typography>
        <Typography component="p" variant="body1" marginBottom={2}>
          OCPF has issued three documents for candidates who are seeking statewide office regarding the Massachusetts Public Financing System. The following documents are available in .pdf file format for viewing and downloading:
        </Typography>
        <Typography component="ul">
          <Typography component="li">
            <Link href="https://ocpf2.blob.core.windows.net/pdf/guides/public_financing_1.pdf" target="_blank" rel="noopener">
              The Application of Spending Limits Declarations
            </Link>
          </Typography>
          <Typography component="li">
            <Link href="https://ocpf2.blob.core.windows.net/pdf/guides/public_financing_2.pdf" target="_blank" rel="noopener">
              Statewide Candidates Expenditure Statements: Filing Requirements
            </Link>
          </Typography>
          <Typography component="li">
            <Link href="https://ocpf2.blob.core.windows.net/pdf/guides/public_financing_3.pdf" target="_blank" rel="noopener">
              An Overview of the Massachusetts Public Financing System
            </Link>
          </Typography>
        </Typography>
      </Box>
      </>
    }
  ];
  
  return (
    <Main>
      <Container>
      <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
             variant="h5"
             align={'left'}
             data-aos={'fade-up'}
             gutterBottom
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            >
            Issues of Interest
        </Typography>
        <Typography 
            variant="subtitle2" 
            data-aos={'fade-up'}
            color="text.secondary"
            gutterBottom
            marginBottom={2}
            >
                This page contains issues that have attracted special interest from the public.
        </Typography>
        <Divider/>
      
          {issues.map((issue, i) => (

            <Accordion sx={{ ":hover": { backgroundColor: '#f5f5f5' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              {issue.title}
            </AccordionSummary>
            <AccordionDetails>
              
                {issue.body}
              
            </AccordionDetails>
            </Accordion>
                     
          ))}
  
      </Container>
    </Main>
  );
};

export default IssuesOfInterest;